/**
 * P0-0b 三档画质预设
 *
 * 设计原则：
 *  1. 只放导演台**真会消费**的参数，不做用不上的预留字段
 *  2. 每档都是自洽可用的整体，不做"任意组合"——组合爆炸是性能问题的来源
 *  3. 允许用户手动覆盖（`overridePreset`），但覆盖只能在探测档位之下收紧，
 *     不能把 low 机器强拉到 high（防用户手动选错后卡死，反过来怪我们）
 */

import type { QualityTier } from './capability';

export interface QualityPreset {
  tier: QualityTier;
  /** UI 展示名 */
  label: string;
  /** 说明文案，直接给用户看，解释这一档牺牲了什么 */
  hint: string;

  /** WebGL 渲染分辨率相对 CSS 像素的倍率 */
  renderScale: number;
  /** 生效的 devicePixelRatio 上限（与 renderScale 相乘后才是实际像素比） */
  dprCap: number;
  /** MSAA。集显上开销明显，low 档关闭 */
  antialias: boolean;
  /** 阴影贴图。专业构图预演基本不需要真实阴影，默认仅 high 开 */
  shadows: boolean;

  /** 地面网格 */
  showGrid: boolean;
  /** 对象头上的名称标签 */
  showLabels: boolean;
  /** 视距渐隐（雾）开关；关闭可省着色器分支 */
  fadeEnabled: boolean;
  /** 地面透明度上限（0 表示不画地面） */
  groundOpacity: number;
  /** 全景球分段数（球面三角数直接决定顶点量） */
  panoramaSegments: readonly [width: number, height: number];

  /** 群众阵列单次实例化上限 */
  crowdMaxInstances: number;
  /**
   * 素体细节级别：
   *  full         完整 65 骨（含手指）
   *  simplified   仅主骨骼，手指不更新
   *  placeholder  低模盒体占位，只保站位与朝向
   */
  bodyLod: 'full' | 'simplified' | 'placeholder';

  /** 截图输出宽度（高度按比例换算） */
  captureWidth: number;
  /** 截图是否携带水印（与旧版产品策略保持一致，此处仅透传开关） */
  captureWatermark: boolean;
}

const PRESETS: Record<QualityTier, QualityPreset> = {
  high: {
    tier: 'high',
    label: '高',
    hint: '完整细节与阴影，适合有独立显卡的机器。',
    renderScale: 1,
    dprCap: 2,
    antialias: true,
    shadows: true,
    showGrid: true,
    showLabels: true,
    fadeEnabled: true,
    groundOpacity: 0.4,
    panoramaSegments: [64, 40],
    crowdMaxInstances: 120,
    bodyLod: 'full',
    captureWidth: 1920,
    captureWatermark: true,
  },
  mid: {
    tier: 'mid',
    label: '中',
    hint: '关闭阴影，全景球与阵列适度收敛，多数近五年笔记本可用。',
    renderScale: 0.85,
    dprCap: 1.5,
    antialias: true,
    shadows: false,
    showGrid: true,
    showLabels: true,
    fadeEnabled: true,
    groundOpacity: 0.4,
    panoramaSegments: [48, 28],
    crowdMaxInstances: 80,
    bodyLod: 'full',
    captureWidth: 1280,
    captureWatermark: true,
  },
  low: {
    tier: 'low',
    label: '流畅',
    hint: '优先保证不卡：关闭网格、渐隐与标签，阵列退化为占位模型。站位与构图判断不受影响。',
    renderScale: 0.7,
    dprCap: 1,
    antialias: false,
    shadows: false,
    showGrid: false,
    showLabels: false,
    fadeEnabled: false,
    groundOpacity: 0,
    panoramaSegments: [32, 18],
    crowdMaxInstances: 24,
    bodyLod: 'placeholder',
    captureWidth: 960,
    captureWatermark: true,
  },
};

/** 档位高低序，用于"只允许向下收紧"的覆盖校验 */
const TIER_ORDER: readonly QualityTier[] = ['low', 'mid', 'high'];

export function tierRank(tier: QualityTier): number {
  return TIER_ORDER.indexOf(tier);
}

export function getQualityPreset(tier: QualityTier): QualityPreset {
  return PRESETS[tier];
}

export function listQualityTiers(): readonly QualityTier[] {
  return TIER_ORDER;
}

/**
 * 解析最终生效档位。
 * @param detected 探测出的档位，作为**上限**
 * @param requested 用户手动选择的档位；高于上限时回退到上限
 */
export function resolvePreset(
  detected: QualityTier,
  requested: QualityTier | null,
): QualityPreset {
  if (requested === null) {
    return getQualityPreset(detected);
  }
  const capped =
    tierRank(requested) <= tierRank(detected) ? requested : detected;
  return getQualityPreset(capped);
}

/**
 * 由档位与实际 DPR 算出画布应使用的像素比。
 * 单独抽出来，避免各处各自 Math.min 导致口径不一致。
 */
export function effectivePixelRatio(preset: QualityPreset, devicePixelRatio: number): number {
  return Math.max(0.5, Math.min(devicePixelRatio, preset.dprCap) * preset.renderScale);
}
